import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * LetterFall — public game component
 * ──────────────────────────────────
 * Port of the standalone prototype at c:\tmp\letter-fall-game-demo.html
 * into the DarijaPro React app.
 *
 * Props:
 *   - API: backend URL prefix
 *   - userToken: JWT for the current user (optional — pro words use it)
 *   - isDarkMode: app-wide dark mode flag (game has its own light/dark mode)
 *   - showNotification: function for toast messages
 *   - onBack: returns to games list
 *   - hasPaidPlan: whether user has Pro membership
 */

// ─────────────────────────────────────────────────────────────────────────────
// Backgrounds (13 hardcoded gradients, matches demo)
// ─────────────────────────────────────────────────────────────────────────────
const BACKGROUNDS = [
  { name: 'White',    gradient: 'linear-gradient(160deg, #ffffff 0%, #e8e8ed 100%)',                 swatch: 'linear-gradient(135deg, #ffffff, #e8e8ed)',                  dark: false },
  { name: 'Dark',     gradient: 'linear-gradient(160deg, #0A0F1C 0%, #1a2235 100%)',                 swatch: 'linear-gradient(135deg, #0A0F1C, #1a2235)',                  dark: true  },
  { name: 'Mint',     gradient: 'linear-gradient(160deg, #E0F2F1 0%, #B2DFDB 100%)',                 swatch: 'linear-gradient(135deg, #E0F2F1, #B2DFDB)',                  dark: false },
  { name: 'Sunset',   gradient: 'linear-gradient(160deg, #FFE5D9 0%, #FFCAD4 50%, #E0BBE4 100%)',    swatch: 'linear-gradient(135deg, #FFE5D9, #FFCAD4, #E0BBE4)',         dark: false },
  { name: 'Ocean',    gradient: 'linear-gradient(160deg, #B3E5FC 0%, #B2EBF2 100%)',                 swatch: 'linear-gradient(135deg, #B3E5FC, #B2EBF2)',                  dark: false },
  { name: 'Sahara',   gradient: 'linear-gradient(160deg, #FFF8E1 0%, #FFE0B2 100%)',                 swatch: 'linear-gradient(135deg, #FFF8E1, #FFE0B2)',                  dark: false },
  { name: 'Blossom',  gradient: 'linear-gradient(160deg, #FCE4EC 0%, #E1BEE7 100%)',                 swatch: 'linear-gradient(135deg, #FCE4EC, #E1BEE7)',                  dark: false },
  { name: 'Tropical', gradient: 'linear-gradient(160deg, #B9F6CA 0%, #B2EBF2 50%, #BBDEFB 100%)',    swatch: 'linear-gradient(135deg, #B9F6CA, #B2EBF2, #BBDEFB)',         dark: false },
  { name: 'Lavender', gradient: 'linear-gradient(160deg, #E8EAF6 0%, #D1C4E9 50%, #F8BBD0 100%)',    swatch: 'linear-gradient(135deg, #E8EAF6, #D1C4E9, #F8BBD0)',         dark: false },
  { name: 'Candy',    gradient: 'linear-gradient(160deg, #FFD6E0 0%, #C5E1FF 100%)',                 swatch: 'linear-gradient(135deg, #FFD6E0, #C5E1FF)',                  dark: false },
  { name: 'Frost',    gradient: 'linear-gradient(160deg, #E1F5FE 0%, #FFFFFF 50%, #B3E5FC 100%)',    swatch: 'linear-gradient(135deg, #E1F5FE, #FFFFFF, #B3E5FC)',         dark: false },
  { name: 'Coral',    gradient: 'linear-gradient(160deg, #FFDDC1 0%, #B2EBF2 100%)',                 swatch: 'linear-gradient(135deg, #FFDDC1, #B2EBF2)',                  dark: false },
  { name: 'Honey',    gradient: 'linear-gradient(160deg, #FFF5BA 0%, #FFE0B2 50%, #FFCDB2 100%)',    swatch: 'linear-gradient(135deg, #FFF5BA, #FFE0B2, #FFCDB2)',         dark: false },
];

const SPEED_PRESETS = {
  slow:   { fall: 0.18, spawn: 700 },
  normal: { fall: 0.28, spawn: 500 },
  fast:   { fall: 0.42, spawn: 350 },
};

const TILE_SIZE = 56;
const MAX_TILES_ON_SCREEN = 5;
const LATIN_POOL = 'abcdefghijklmnopqrstuvwxyz';
const ARABIC_POOL = 'ابتثجحخدذرزسشصضطظعغفقكلمنهويءأإآةىئ';

// ─────────────────────────────────────────────────────────────────────────────
// Sound engine (Web Audio, lazy init)
// ─────────────────────────────────────────────────────────────────────────────
function makeSoundEngine() {
  let ctx = null;
  let enabled = localStorage.getItem('letterFallSound') !== 'off';
  const get = () => {
    if (ctx) return ctx;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    } catch { return null; }
    return ctx;
  };
  const tone = (freq, durMs, type = 'sine', vol = 0.18, off = 0) => {
    if (!enabled) return;
    const c = get(); if (!c) return;
    const t0 = c.currentTime + off / 1000;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(vol, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + durMs / 1000);
    o.connect(g).connect(c.destination);
    o.start(t0);
    o.stop(t0 + durMs / 1000 + 0.02);
  };
  const noise = (durMs, vol = 0.10) => {
    if (!enabled) return;
    const c = get(); if (!c) return;
    const buf = c.createBuffer(1, c.sampleRate * (durMs / 1000), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = c.createBufferSource(); src.buffer = buf;
    const f = c.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 800; f.Q.value = 0.8;
    const g = c.createGain(); g.gain.value = vol;
    src.connect(f).connect(g).connect(c.destination);
    src.start(); src.stop(c.currentTime + durMs / 1000);
  };
  return {
    init: () => { get(); },
    isEnabled: () => enabled,
    setEnabled: (v) => { enabled = v; localStorage.setItem('letterFallSound', v ? 'on' : 'off'); },
    correct: () => { tone(660, 100, 'sine', 0.18); tone(880, 100, 'sine', 0.14); },
    wrong:   () => { tone(200, 90, 'sawtooth', 0.14); },
    complete: () => { tone(523.25, 120, 'sine', 0.18, 0); tone(659.25, 120, 'sine', 0.18, 110); tone(783.99, 250, 'sine', 0.22, 220); },
    skip:    () => { noise(140, 0.10); },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS — exactly mirrors the prototype, served via <style> tag
// ─────────────────────────────────────────────────────────────────────────────
const STYLE = `
/* Desktop (≥1024px): in-page container, app sidebar stays visible.
   Mobile (<1024px): full-screen takeover for distraction-free play. */
.lf-host {
  position: relative;
  width: 100%;
  height: calc(100vh - 80px);
  min-height: 600px;
  overflow: hidden;
  border-radius: 16px;
  font-family: 'Inter', -apple-system, sans-serif; color: #0A0F1C;
  touch-action: manipulation; user-select: none; -webkit-user-select: none;
  transition: background 0.4s ease;
}
@media (max-width: 1023px) {
  .lf-host {
    position: fixed; inset: 0;
    z-index: 1000; height: 100vh;
    border-radius: 0;
  }
}
.lf-host * { box-sizing: border-box; }
.lf-host .fx-layer { position: absolute; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; border-radius: inherit; }
.lf-host .fx-orb { position: absolute; border-radius: 50%; filter: blur(40px); will-change: transform, opacity; }
.lf-host .fx-star { position: absolute; border-radius: 50%; background: white; }

/* Theme 0 White */
.lf-bg-0 .fx-orb.o1 { width: 320px; height: 320px; background: #B299E0; top: -10%; left: -10%; opacity: 0.12; animation: lf-drift1 22s ease-in-out infinite; }
.lf-bg-0 .fx-orb.o2 { width: 360px; height: 360px; background: #93D3B5; bottom: -12%; right: -12%; opacity: 0.12; animation: lf-drift2 26s ease-in-out infinite; }
.lf-bg-0 .fx-orb.o3 { width: 260px; height: 260px; background: #B3E5FC; top: 40%; right: -8%; opacity: 0.14; animation: lf-drift3 24s ease-in-out infinite; }
.lf-bg-0 .fx-star { background: rgba(178,153,224,0.7); box-shadow: 0 0 6px rgba(178,153,224,0.5); }
.lf-bg-0 .fx-star.s1 { width: 3px; height: 3px; top: 18%; left: 24%; animation: lf-twinkle 3.0s ease-in-out infinite; }
.lf-bg-0 .fx-star.s2 { width: 2px; height: 2px; top: 32%; left: 76%; animation: lf-twinkle 4.2s ease-in-out infinite 0.4s; }
.lf-bg-0 .fx-star.s3 { width: 2.5px; height: 2.5px; top: 56%; left: 18%; background: rgba(147,211,181,0.7); box-shadow: 0 0 6px rgba(147,211,181,0.5); animation: lf-twinkle 3.6s ease-in-out infinite 1.1s; }
.lf-bg-0 .fx-star.s4 { width: 2px; height: 2px; top: 70%; left: 80%; background: rgba(179,229,252,0.8); box-shadow: 0 0 6px rgba(179,229,252,0.6); animation: lf-twinkle 2.8s ease-in-out infinite 0.8s; }

/* Theme 1 Dark — stars + aurora + shooting */
.lf-bg-1 .fx-layer { background: radial-gradient(ellipse at top, rgba(122,162,255,0.10) 0%, transparent 50%), radial-gradient(ellipse at bottom right, rgba(178,153,224,0.10) 0%, transparent 60%); }
.lf-bg-1 .fx-orb.o1 { width: 480px; height: 480px; background: #3B6CEC; top: -20%; left: -20%; opacity: 0.18; animation: lf-aurora 22s ease-in-out infinite; }
.lf-bg-1 .fx-orb.o2 { width: 420px; height: 420px; background: #B299E0; top: 40%; right: -25%; opacity: 0.20; animation: lf-aurora 26s ease-in-out infinite reverse; }
.lf-bg-1 .fx-orb.o3 { width: 360px; height: 360px; background: #E96B9D; bottom: -25%; left: 25%; opacity: 0.15; animation: lf-aurora 28s ease-in-out infinite 4s; }
.lf-bg-1 .fx-star { background: white; box-shadow: 0 0 6px white; }
.lf-bg-1 .fx-star.s1 { width: 2px; height: 2px; top: 8%; left: 12%; animation: lf-twinkle 3.0s ease-in-out infinite; }
.lf-bg-1 .fx-star.s2 { width: 3px; height: 3px; top: 18%; left: 78%; animation: lf-twinkle 4.2s ease-in-out infinite 0.4s; }
.lf-bg-1 .fx-star.s3 { width: 1.5px; height: 1.5px; top: 32%; left: 24%; animation: lf-twinkle 2.6s ease-in-out infinite 0.8s; }
.lf-bg-1 .fx-star.s4 { width: 2px; height: 2px; top: 56%; left: 88%; animation: lf-twinkle 3.4s ease-in-out infinite 1.2s; }
.lf-bg-1 .fx-star.s5 { width: 2.5px; height: 2.5px; top: 72%; left: 18%; animation: lf-twinkle 3.8s ease-in-out infinite 0.6s; }
.lf-bg-1 .fx-star.s6 { width: 1.5px; height: 1.5px; top: 84%; left: 64%; animation: lf-twinkle 2.4s ease-in-out infinite 1.5s; }
.lf-bg-1 .fx-star.s7 { width: 2px; height: 2px; top: 44%; left: 56%; animation: lf-twinkle 4.8s ease-in-out infinite 0.3s; }
.lf-bg-1 .fx-star.s8 { width: 3px; height: 3px; top: 22%; left: 44%; animation: lf-twinkle 3.2s ease-in-out infinite 1.0s; }
.lf-bg-1 .shooting { position: absolute; top: 22%; left: -10%; width: 140px; height: 1.5px; background: linear-gradient(90deg, transparent, white); filter: drop-shadow(0 0 6px white); animation: lf-shoot 7s ease-out infinite; }

/* Themes 2-12 (mint, sunset, ocean, sahara, blossom, tropical, lavender, candy, frost, coral, honey) */
.lf-bg-2 .fx-orb.o1 { width: 340px; height: 340px; background: #80CBC4; top: -10%; left: -8%; opacity: 0.45; animation: lf-drift1 22s ease-in-out infinite; }
.lf-bg-2 .fx-orb.o2 { width: 380px; height: 380px; background: #B2EBF2; bottom: -12%; right: -10%; opacity: 0.40; animation: lf-drift2 26s ease-in-out infinite; }
.lf-bg-2 .fx-orb.o3 { width: 240px; height: 240px; background: #C8E6C9; top: 38%; right: -10%; opacity: 0.40; animation: lf-drift3 24s ease-in-out infinite; }

.lf-bg-3 .fx-orb.o1 { width: 340px; height: 340px; background: #F5B68F; top: -8%; left: -5%; opacity: 0.50; animation: lf-drift1 18s ease-in-out infinite; }
.lf-bg-3 .fx-orb.o2 { width: 420px; height: 420px; background: #E96B9D; bottom: -15%; right: -8%; opacity: 0.45; animation: lf-drift2 24s ease-in-out infinite; }
.lf-bg-3 .fx-orb.o3 { width: 280px; height: 280px; background: #B299E0; top: 40%; left: 50%; opacity: 0.35; animation: lf-drift3 21s ease-in-out infinite; }

.lf-bg-4 .fx-layer { background: repeating-linear-gradient(95deg, transparent 0 60px, rgba(255,255,255,0.05) 60px 62px), repeating-linear-gradient(85deg, transparent 0 80px, rgba(255,255,255,0.04) 80px 82px); animation: lf-wave 18s linear infinite; }
.lf-bg-4 .fx-orb.o1 { width: 280px; height: 280px; background: #93D3B5; top: 10%; right: -10%; opacity: 0.30; animation: lf-drift1 22s ease-in-out infinite; }
.lf-bg-4 .fx-orb.o2 { width: 220px; height: 220px; background: #3B6CEC; bottom: -10%; left: -10%; opacity: 0.30; animation: lf-drift2 26s ease-in-out infinite; }

.lf-bg-5 .fx-layer { background-image: radial-gradient(circle, rgba(255,240,200,0.25) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(255,220,170,0.18) 1px, transparent 1px); background-size: 60px 60px, 90px 90px; animation: lf-sand 30s linear infinite; }
.lf-bg-5 .fx-orb.o1 { width: 360px; height: 360px; background: #c9a86a; top: 20%; right: -15%; opacity: 0.35; animation: lf-drift1 20s ease-in-out infinite; }

.lf-bg-6 .fx-orb.o1 { width: 320px; height: 320px; background: #FFB7C5; top: -5%; right: 30%; opacity: 0.40; animation: lf-drift1 20s ease-in-out infinite; }

.lf-bg-7 .fx-orb.o1 { width: 260px; height: 260px; background: #00cec9; top: -8%; right: -10%; opacity: 0.45; animation: lf-bubble 18s ease-in-out infinite; }
.lf-bg-7 .fx-orb.o2 { width: 200px; height: 200px; background: #0984e3; bottom: -12%; left: 20%; opacity: 0.45; animation: lf-bubble 22s ease-in-out infinite reverse; }
.lf-bg-7 .fx-orb.o3 { width: 180px; height: 180px; background: #00b894; top: 50%; right: 60%; opacity: 0.40; animation: lf-bubble 20s ease-in-out infinite 4s; }
.lf-bg-7 .fx-orb.o4 { width: 140px; height: 140px; background: #00cec9; bottom: 20%; right: -5%; opacity: 0.40; animation: lf-bubble 16s ease-in-out infinite 2s; }

.lf-bg-8 .fx-orb.o1 { width: 380px; height: 380px; background: #B39DDB; top: -15%; left: -10%; opacity: 0.45; animation: lf-aurora 18s ease-in-out infinite; }
.lf-bg-8 .fx-orb.o2 { width: 340px; height: 340px; background: #F8BBD0; top: 35%; right: -15%; opacity: 0.45; animation: lf-aurora 22s ease-in-out infinite reverse; }
.lf-bg-8 .fx-orb.o3 { width: 280px; height: 280px; background: #C5CAE9; bottom: -15%; left: 25%; opacity: 0.40; animation: lf-aurora 20s ease-in-out infinite 3s; }

.lf-bg-9 .fx-orb.o1 { width: 360px; height: 360px; background: #FF9BB5; top: -10%; left: -8%; opacity: 0.50; animation: lf-drift1 20s ease-in-out infinite; }
.lf-bg-9 .fx-orb.o2 { width: 320px; height: 320px; background: #89C4FF; bottom: -10%; right: -8%; opacity: 0.50; animation: lf-drift2 24s ease-in-out infinite; }
.lf-bg-9 .fx-orb.o3 { width: 240px; height: 240px; background: #FFC9DD; top: 40%; right: 55%; opacity: 0.45; animation: lf-drift3 22s ease-in-out infinite; }

.lf-bg-10 .fx-orb.o1 { width: 360px; height: 360px; background: #B3E5FC; top: -10%; left: -8%; opacity: 0.35; animation: lf-drift1 22s ease-in-out infinite; }
.lf-bg-10 .fx-orb.o2 { width: 280px; height: 280px; background: #E1F5FE; bottom: -10%; right: 20%; opacity: 0.55; animation: lf-drift2 26s ease-in-out infinite; }
.lf-bg-10 .fx-star { background: #ffffff; box-shadow: 0 0 8px rgba(179,229,252,0.8); }
.lf-bg-10 .fx-star.s1 { width: 3px; height: 3px; top: 14%; left: 20%; animation: lf-twinkle 3.2s ease-in-out infinite; }
.lf-bg-10 .fx-star.s2 { width: 2px; height: 2px; top: 26%; left: 78%; animation: lf-twinkle 4.0s ease-in-out infinite 0.5s; }
.lf-bg-10 .fx-star.s3 { width: 2.5px; height: 2.5px; top: 48%; left: 14%; animation: lf-twinkle 3.6s ease-in-out infinite 1.1s; }
.lf-bg-10 .fx-star.s4 { width: 2px; height: 2px; top: 62%; left: 68%; animation: lf-twinkle 2.8s ease-in-out infinite 0.8s; }
.lf-bg-10 .fx-star.s5 { width: 3px; height: 3px; top: 78%; left: 30%; animation: lf-twinkle 4.5s ease-in-out infinite 0.3s; }
.lf-bg-10 .fx-star.s6 { width: 2px; height: 2px; top: 86%; left: 82%; animation: lf-twinkle 3.4s ease-in-out infinite 1.5s; }

.lf-bg-11 .fx-orb.o1 { width: 240px; height: 240px; background: #FFAB91; top: -8%; right: -10%; opacity: 0.45; animation: lf-bubble 18s ease-in-out infinite; }
.lf-bg-11 .fx-orb.o2 { width: 200px; height: 200px; background: #80DEEA; bottom: -12%; left: 20%; opacity: 0.45; animation: lf-bubble 22s ease-in-out infinite reverse; }
.lf-bg-11 .fx-orb.o3 { width: 180px; height: 180px; background: #FFCCBC; top: 50%; right: 55%; opacity: 0.40; animation: lf-bubble 20s ease-in-out infinite 4s; }
.lf-bg-11 .fx-orb.o4 { width: 140px; height: 140px; background: #80DEEA; bottom: 25%; right: -5%; opacity: 0.40; animation: lf-bubble 16s ease-in-out infinite 2s; }

.lf-bg-12 .fx-layer { background-image: radial-gradient(circle, rgba(255,235,180,0.30) 1.5px, transparent 1.5px); background-size: 80px 80px; animation: lf-sand 45s linear infinite; }
.lf-bg-12 .fx-orb.o1 { width: 380px; height: 380px; background: #FFD180; top: -12%; left: -10%; opacity: 0.50; animation: lf-drift1 24s ease-in-out infinite; }
.lf-bg-12 .fx-orb.o2 { width: 320px; height: 320px; background: #FFE0B2; bottom: -10%; right: -10%; opacity: 0.55; animation: lf-drift2 28s ease-in-out infinite; }
.lf-bg-12 .fx-orb.o3 { width: 260px; height: 260px; background: #FFCDB2; top: 45%; right: 50%; opacity: 0.45; animation: lf-drift3 22s ease-in-out infinite; }

@keyframes lf-drift1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(40px, -30px); } }
@keyframes lf-drift2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-50px, 40px); } }
@keyframes lf-drift3 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(30px, 60px); } }
@keyframes lf-twinkle { 0%,100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.6); } }
@keyframes lf-shoot { 0%,80% { transform: translate(0,0) rotate(-20deg); opacity: 0; } 82% { opacity: 1; } 100% { transform: translate(1200px, 400px) rotate(-20deg); opacity: 0; } }
@keyframes lf-wave { 0% { background-position: 0 0, 0 0; } 100% { background-position: 200px 0, -240px 0; } }
@keyframes lf-sand { 0% { background-position: 0 0; } 100% { background-position: 600px 0; } }
@keyframes lf-bubble { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(20px, -30px) scale(1.1); } }
@keyframes lf-aurora { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(40px, -30px) scale(1.1); } 66% { transform: translate(-30px, 40px) scale(0.95); } }

.lf-topnav { position: absolute; top: 0; left: 0; right: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  background: rgba(255,255,255,0.50);
  backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(10,15,28,0.06);
  border-top-left-radius: inherit; border-top-right-radius: inherit;
}
.lf-host.lf-dark .lf-topnav { background: rgba(20,28,46,0.6); border-bottom-color: rgba(255,255,255,0.10); }
.lf-back-btn, .lf-gear-btn {
  background: rgba(10,15,28,0.10); border: none; cursor: pointer;
  width: 36px; height: 36px; border-radius: 50%;
  color: #0A0F1C; display: inline-flex; align-items: center; justify-content: center;
  padding: 0; flex-shrink: 0;
  transition: transform 0.2s, background 0.2s;
}
.lf-host.lf-dark .lf-back-btn, .lf-host.lf-dark .lf-gear-btn { background: rgba(255,255,255,0.10); color: white; }
.lf-back-btn:hover, .lf-gear-btn:hover { transform: scale(1.08); background: rgba(10,15,28,0.20); }
.lf-host.lf-dark .lf-back-btn:hover, .lf-host.lf-dark .lf-gear-btn:hover { background: rgba(255,255,255,0.20); }
.lf-gear-btn:hover { transform: rotate(45deg) scale(1.05); }

.lf-speed-switcher { display: flex; gap: 4px;
  background: rgba(10,15,28,0.12); border-radius: 999px;
  padding: 3px; margin-left: auto; margin-right: auto;
}
.lf-host.lf-dark .lf-speed-switcher { background: rgba(255,255,255,0.10); }
.lf-speed-switcher button {
  border: none; background: transparent; cursor: pointer;
  padding: 5px 10px; border-radius: 999px;
  font-family: 'Space Grotesk', sans-serif; font-weight: 600;
  font-size: 11px; color: rgba(10,15,28,0.65);
  letter-spacing: 0.5px; text-transform: uppercase;
  transition: all 0.15s;
}
.lf-host.lf-dark .lf-speed-switcher button { color: rgba(255,255,255,0.7); }
.lf-speed-switcher button.active { background: linear-gradient(135deg, #3B6CEC, #B299E0); color: white; box-shadow: 0 4px 12px rgba(59,108,236,0.40); }

.lf-header { position: absolute; top: 80px; left: 0; right: 0;
  text-align: center; padding: 0 20px; pointer-events: none; z-index: 10;
}
@media (max-width: 540px) { .lf-header { top: 90px; } }
.lf-header .label {
  display: inline-block;
  font-family: 'Space Grotesk', sans-serif; font-weight: 700;
  font-size: 11px; color: rgba(255,255,255,0.95);
  letter-spacing: 2px; text-transform: uppercase;
  background: rgba(10,15,28,0.85); padding: 5px 14px; border-radius: 999px;
  margin-bottom: 12px;
}
.lf-host.lf-dark .lf-header .label { background: rgba(255,255,255,0.15); color: white; }
/* English meaning pill — TOP, very prominent */
.lf-header .target-meaning {
  display: inline-block;
  font-size: 22px; color: #0A0F1C;
  font-weight: 700;
  letter-spacing: 0.2px;
  background: rgba(255,255,255,0.85);
  padding: 8px 22px;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(10,15,28,0.10);
  border: 1px solid rgba(10,15,28,0.08);
  margin: 0 0 14px;
}
.lf-host.lf-dark .lf-header .target-meaning {
  color: white;
  background: rgba(255,255,255,0.15);
  border-color: rgba(255,255,255,0.20);
  box-shadow: 0 4px 16px rgba(0,0,0,0.30);
}
@media (max-width: 540px) { .lf-header .target-meaning { font-size: 18px; padding: 6px 18px; margin-bottom: 10px; } }

/* Darija target — under English */
.lf-header .target-word {
  font-family: 'Space Grotesk', sans-serif; font-weight: 700;
  font-size: clamp(36px, 9vw, 52px);
  color: #0A0F1C; letter-spacing: 4px;
  text-shadow: 0 2px 8px rgba(255,255,255,0.7);
  margin: 0;
}
.lf-host.lf-dark .lf-header .target-word { color: white; text-shadow: 0 4px 16px rgba(0,0,0,0.50); }
.lf-header .target-word.arabic { font-family: 'Noto Naskh Arabic', serif; letter-spacing: 0; direction: rtl; unicode-bidi: bidi-override; }

.lf-fall-area {
  position: absolute; top: 240px; left: 14px; right: 14px; bottom: 160px;
  overflow: hidden; border-radius: 24px;
  background: rgba(10,15,28,0.05);
  border: 1px solid rgba(10,15,28,0.12);
}
.lf-host.lf-dark .lf-fall-area { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.10); }
@media (max-width: 540px) { .lf-fall-area { top: 250px; } }

.lf-letter-tile {
  position: absolute; width: 56px; height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffffff, #f5f5f8);
  color: #0A0F1C;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 800; font-size: 28px;
  display: grid; place-items: center;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(10,15,28,0.18), inset 0 -3px 0 rgba(10,15,28,0.05);
  border: 1.5px solid rgba(10,15,28,0.10);
  will-change: transform; contain: layout style paint;
}
.lf-letter-tile.arabic { font-family: 'Noto Naskh Arabic', serif; font-size: 30px; }
.lf-letter-tile.correct { background: #c0eb6f; box-shadow: 0 8px 24px rgba(10,15,28,0.20), 0 0 0 4px rgba(150,220,80,0.6); transition: background 0.25s, box-shadow 0.25s; }
.lf-letter-tile.wrong { background: #ffd5d5; color: #c0392b; box-shadow: 0 8px 24px rgba(10,15,28,0.20), 0 0 0 4px rgba(231,76,60,0.55); transition: background 0.15s, box-shadow 0.15s; }

.lf-slots {
  position: absolute; bottom: 80px; left: 0; right: 0;
  display: flex; justify-content: center; gap: 10px;
  padding: 0 14px; flex-wrap: wrap;
}
/* Arabic mode: first slot is on the RIGHT, fills right-to-left as user catches letters */
.lf-slots.arabic { direction: rtl; }
.lf-slot {
  width: 52px; height: 60px; border-radius: 14px;
  background: rgba(10,15,28,0.10);
  border: 2px dashed rgba(10,15,28,0.30);
  display: grid; place-items: center;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 800; font-size: 28px;
  color: #0A0F1C;
}
.lf-slot.arabic { font-family: 'Noto Naskh Arabic', serif; font-size: 30px; }
.lf-host.lf-dark .lf-slot { background: rgba(255,255,255,0.10); border-color: rgba(255,255,255,0.35); color: white; }
.lf-slot.filled { background: #0A0F1C; color: white; border: 2px solid transparent; box-shadow: 0 6px 18px rgba(10,15,28,0.30); animation: lf-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
.lf-host.lf-dark .lf-slot.filled { background: white; color: #0A0F1C; }
@keyframes lf-pop { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }

.lf-bottom { position: absolute; bottom: 18px; left: 0; right: 0;
  display: flex; justify-content: center; align-items: center; gap: 10px;
  padding: 0 20px; z-index: 20;
}
.lf-btn { display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 24px; border-radius: 999px;
  font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 14px;
  border: none; cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
}
.lf-btn-skip {
  background: linear-gradient(135deg, #0A0F1C, #1a2235);
  color: white;
  box-shadow: 0 6px 16px rgba(10,15,28,0.20);
}
.lf-btn-skip:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(10,15,28,0.30); }

/* Settings drawer */
.lf-drawer { position: absolute; inset: 0; z-index: 250; pointer-events: none; visibility: hidden; border-radius: inherit; overflow: hidden; }
.lf-drawer.show { pointer-events: auto; visibility: visible; }
.lf-drawer-overlay { position: absolute; inset: 0; background: rgba(10,15,28,0.55); opacity: 0; transition: opacity 0.25s; }
.lf-drawer.show .lf-drawer-overlay { opacity: 1; }
.lf-drawer-panel { position: absolute; top: 0; right: 0; bottom: 0; width: 84%; max-width: 340px;
  background: white; box-shadow: -10px 0 40px rgba(10,15,28,0.20);
  transform: translateX(100%); transition: transform 0.30s cubic-bezier(0.32, 0.72, 0.30, 1);
  display: flex; flex-direction: column;
}
.lf-drawer.show .lf-drawer-panel { transform: translateX(0); }
.lf-drawer-head { display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px; border-bottom: 1px solid rgba(10,15,28,0.08);
}
.lf-drawer-head h2 { margin: 0; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 18px; color: #0A0F1C; }
.lf-drawer-close { width: 36px; height: 36px; border-radius: 50%; background: rgba(10,15,28,0.06); border: none; color: #0A0F1C; cursor: pointer; font-size: 16px; }
/* Desktop topnav right group — keeps "?" and gear together with breathing room */
.lf-topnav-right { display: flex; align-items: center; gap: 14px; }
.lf-help-btn { width: 36px; height: 36px; border-radius: 50%; background: rgba(10,15,28,0.10); border: none; color: #0A0F1C; cursor: pointer; display: grid; place-items: center; transition: background 0.15s, transform 0.15s; }
.lf-help-btn:hover { background: rgba(10,15,28,0.20); transform: scale(1.08); }
.lf-host.lf-dark .lf-help-btn { background: rgba(255,255,255,0.10); color: white; }
.lf-host.lf-dark .lf-help-btn:hover { background: rgba(255,255,255,0.20); }
/* Mobile drawer "How to play" — at the BOTTOM of the panel, far from the gear */
.lf-drawer-help-section { margin-top: 8px; padding-top: 18px; border-top: 1px solid rgba(10,15,28,0.08); }
.lf-drawer-help-btn { width: 100%; display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 14px; background: linear-gradient(135deg, rgba(59,108,236,0.10), rgba(178,153,224,0.10)); border: 1px solid rgba(59,108,236,0.20); color: #0A0F1C; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px; cursor: pointer; transition: transform 0.15s, box-shadow 0.2s; }
.lf-drawer-help-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(59,108,236,0.20); }
.lf-drawer-help-btn .ic { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 50%; background: linear-gradient(135deg, #3B6CEC, #B299E0); color: white; font-weight: 800; font-size: 16px; }
/* Help popup close button (corner X) */
.lf-help-close { position: absolute; top: 14px; right: 14px; width: 32px; height: 32px; border-radius: 50%; background: rgba(10,15,28,0.06); border: none; color: #0A0F1C; cursor: pointer; font-size: 14px; display: grid; place-items: center; }
.lf-help-close:hover { background: rgba(10,15,28,0.12); }
.lf-intro-card { position: relative; }
/* Desktop-only "?" in the topnav. On mobile it's hidden — drawer button takes over. */
@media (max-width: 1023px) { .lf-help-btn-desktop { display: none; } }
.lf-drawer-body { flex: 1; padding: 20px 22px; overflow-y: auto; }
.lf-drawer-section { margin-bottom: 24px; }
.lf-drawer-section .label { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 11px; color: #8A8E9A; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 10px; }
.lf-bg-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.lf-bg-grid button { aspect-ratio: 1; border-radius: 14px; border: 2px solid rgba(10,15,28,0.10); cursor: pointer; padding: 0; position: relative; transition: transform 0.2s, border-color 0.15s; }
.lf-bg-grid button:hover { transform: scale(1.05); }
.lf-bg-grid button.active { border-color: #3B6CEC; transform: scale(1.05); box-shadow: 0 0 0 3px rgba(59,108,236,0.20); }
.lf-bg-grid .name { position: absolute; bottom: 4px; left: 0; right: 0; font-size: 9px; font-weight: 700; color: white; text-shadow: 0 1px 3px rgba(0,0,0,0.5); font-family: 'Space Grotesk', sans-serif; }
.lf-bg-grid button[data-bg="0"] .name { color: #0A0F1C; text-shadow: 0 1px 3px rgba(255,255,255,0.8); }
.lf-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; background: rgba(10,15,28,0.04); border-radius: 12px; }
.lf-row .label-text { font-size: 14px; font-weight: 600; color: #0A0F1C; }
.lf-row .hint { font-size: 11px; color: #8A8E9A; margin-top: 2px; }
.lf-toggle { width: 46px; height: 26px; border-radius: 13px; background: rgba(10,15,28,0.20); border: none; cursor: pointer; padding: 0; position: relative; transition: background 0.2s; }
.lf-toggle::after { content: ''; position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 50%; background: white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); transition: transform 0.2s; }
.lf-toggle.on { background: linear-gradient(135deg, #3B6CEC, #B299E0); }
.lf-toggle.on::after { transform: translateX(20px); }
.lf-script-radio { display: flex; gap: 6px; }
.lf-script-radio button { flex: 1; padding: 10px; border-radius: 10px; border: 2px solid rgba(10,15,28,0.10); background: white; cursor: pointer; font-weight: 600; font-size: 13px; color: #0A0F1C; }
.lf-script-radio button.active { background: linear-gradient(135deg, #3B6CEC, #B299E0); color: white; border-color: transparent; }

/* Complete + empty overlays */
.lf-overlay { position: absolute; inset: 0; z-index: 100; background: rgba(10,15,28,0.55); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; border-radius: inherit; }

/* Welcome / intro overlay — first thing user sees */
.lf-intro { position: absolute; inset: 0; z-index: 200; background: rgba(10,15,28,0.70); backdrop-filter: blur(14px); display: flex; align-items: center; justify-content: center; padding: 20px; border-radius: inherit; animation: lf-intro-in 0.35s ease; }
@keyframes lf-intro-in { from { opacity: 0; } to { opacity: 1; } }
.lf-intro-card { background: white; border-radius: 28px; padding: 36px 28px 30px; text-align: center; max-width: 380px; width: 92%; box-shadow: 0 30px 80px rgba(0,0,0,0.45); animation: lf-intro-card-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes lf-intro-card-in { from { transform: scale(0.6) translateY(20px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }
/* Fancy hero — 3 colored mini-tiles (A B C) like the games-list icon */
.lf-intro-hero { position: relative; width: 130px; height: 80px; margin: 0 auto 14px; }
.lf-intro-mini { position: absolute; width: 38px; height: 38px; border-radius: 11px; display: grid; place-items: center; font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 22px; color: white; box-shadow: 0 6px 14px rgba(10,15,28,0.20), inset 0 -2px 0 rgba(0,0,0,0.10); animation: lf-intro-bob 2.6s ease-in-out infinite; }
.lf-intro-mini.a { background: linear-gradient(135deg, #F5B68F, #E96B9D); left: 6px; top: 28px; transform: rotate(-8deg); animation-delay: 0s; }
.lf-intro-mini.b { background: linear-gradient(135deg, #3B6CEC, #B299E0); left: 46px; top: 8px; animation-delay: 0.3s; }
.lf-intro-mini.c { background: linear-gradient(135deg, #93D3B5, #3B6CEC); left: 86px; top: 32px; transform: rotate(10deg); animation-delay: 0.6s; }
@keyframes lf-intro-bob { 0%,100% { transform: translateY(0) rotate(var(--rot, 0deg)); } 50% { transform: translateY(-8px) rotate(var(--rot, 0deg)); } }
.lf-intro-mini.a { --rot: -8deg; }
.lf-intro-mini.c { --rot: 10deg; }
.lf-intro-card h2 { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 30px; color: #0A0F1C; margin: 0 0 8px; letter-spacing: -0.5px; }
.lf-intro-card .sub { font-size: 15px; color: #8A8E9A; margin: 0 0 22px; line-height: 1.55; }
.lf-intro-how { text-align: left; background: linear-gradient(135deg, rgba(59,108,236,0.06), rgba(178,153,224,0.06)); border-radius: 16px; padding: 18px 20px; font-size: 14px; color: #2C3441; line-height: 1.75; margin: 0 0 24px; list-style: none; }
.lf-intro-how li { margin: 0 0 8px; display: flex; align-items: flex-start; gap: 8px; }
.lf-intro-how li:last-child { margin-bottom: 0; }
.lf-intro-how li .ic { flex-shrink: 0; width: 22px; text-align: center; }
.lf-intro-how strong { color: #0A0F1C; font-weight: 700; }
.lf-overlay-card { background: white; border-radius: 28px; padding: 36px 28px; text-align: center; max-width: 320px; width: 86%; box-shadow: 0 30px 80px rgba(0,0,0,0.40); animation: lf-card-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes lf-card-in { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.lf-overlay-card .emoji { font-size: 56px; margin-bottom: 6px; line-height: 1; }
.lf-overlay-card h2 { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 28px; color: #0A0F1C; margin: 4px 0; }
.lf-overlay-card .sub { font-size: 14px; color: #8A8E9A; margin-bottom: 22px; line-height: 1.5; }
.lf-overlay-card .word { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 38px; color: #0A0F1C; letter-spacing: 3px; margin: 8px 0 4px; }
.lf-overlay-card .word.arabic { font-family: 'Noto Naskh Arabic', serif; letter-spacing: 0; }
.lf-overlay-card .meaning { font-size: 14px; color: #8A8E9A; margin-bottom: 22px; }
.lf-overlay-card .btn-primary { background: linear-gradient(135deg, #3B6CEC, #B299E0); color: white; padding: 14px 28px; border-radius: 999px; border: none; cursor: pointer; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px; box-shadow: 0 10px 28px rgba(59,108,236,0.40); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const LetterFall = ({ API, userToken, isDarkMode, showNotification, onBack, hasPaidPlan }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [words, setWords] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  // Game runtime state — initialized from localStorage so user's choice persists
  // across re-mounts, navigation, and refreshes. Falls back to admin defaults
  // (loaded async below) only if user hasn't picked anything yet.
  const [bgIdx, setBgIdxState] = useState(() => {
    try { const v = localStorage.getItem('letterFallBg'); return v === null ? 0 : Number(v); } catch { return 0; }
  });
  const [script, setScriptState] = useState(() => {
    try { return localStorage.getItem('letterFallScript') || 'latin'; } catch { return 'latin'; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [speed, setSpeedState] = useState(() => {
    try { return localStorage.getItem('letterFallSpeed') || 'slow'; } catch { return 'slow'; }
  });

  // Persist preferences on every change (survives re-mounts)
  const setBgIdx = (v) => { setBgIdxState(v); try { localStorage.setItem('letterFallBg', String(v)); } catch {} };
  const setScript = (v) => { setScriptState(v); try { localStorage.setItem('letterFallScript', v); } catch {} };
  const setSpeed = (v) => { setSpeedState(v); try { localStorage.setItem('letterFallSpeed', v); } catch {} };

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [filledSlots, setFilledSlots] = useState([]);
  const [showComplete, setShowComplete] = useState(false);
  // "How to play" popup — opened via the "?" button in the header (desktop)
  // or the "How to play" row at the bottom of the settings drawer (mobile)
  const [showHelp, setShowHelp] = useState(false);

  // Refs
  const fallAreaRef = useRef(null);
  const slotsRef = useRef(null);
  const fallingTilesRef = useRef([]);
  const animationRef = useRef(null);
  const lastFrameRef = useRef(0);
  const lastSpawnRef = useRef(0);
  const sfxRef = useRef(null);
  const nextLetterIndexRef = useRef(0);
  const sinceNeededRef = useRef(0);
  const currentWordIndexRef = useRef(0);

  // Init sound engine once
  useEffect(() => {
    sfxRef.current = makeSoundEngine();
    setSoundEnabled(sfxRef.current.isEnabled());
  }, []);

  // Load settings + words — runs ONCE on mount. Don't include showNotification
  // in deps (parent recreates it every render, which would loop the load + reset
  // user's chosen background back to admin default).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
        const [sR, wR] = await Promise.all([
          fetch(`${API}/letter-fall/game-settings`),
          fetch(`${API}/letter-fall/words`, { headers }),
        ]);
        if (cancelled) return;
        if (!sR.ok) throw new Error('Failed to load settings');
        const s = await sR.json();
        setSettings(s);
        // Only use admin defaults if user has NEVER picked their own preference
        if (localStorage.getItem('letterFallScript') === null) setScriptState(s.default_script || 'latin');
        if (localStorage.getItem('letterFallBg') === null) setBgIdxState(s.active_background_id ?? 0);
        if (wR.status === 403) { setErrorMsg('pro_required'); return; }
        if (!wR.ok) throw new Error('Failed to load words');
        const w = await wR.json();
        if (!w.words || w.words.length === 0) { setErrorMsg('no_words'); return; }
        const shuffled = [...w.words].sort(() => Math.random() - 0.5);
        setWords(shuffled);
      } catch (e) {
        if (cancelled) return;
        setErrorMsg('load_error');
        showNotification?.(`Letter Fall: ${e.message}`, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // showNotification intentionally excluded — see comment above
  }, [API, userToken]);

  // Sync ref to state
  useEffect(() => { currentWordIndexRef.current = currentWordIndex; }, [currentWordIndex]);

  // Current word + target string
  const currentWord = words[currentWordIndex] || null;
  const targetString = currentWord ? (script === 'arabic' ? currentWord.arabic : currentWord.latin).toLowerCase() : '';
  const isArabicMode = script === 'arabic';
  // Pool is always alphabet + digits, regardless of target word content.
  // Reason: when a word has a digit (e.g. "level 2"), digits would otherwise
  // only start appearing partway through that word. Mixing digits in always
  // keeps the falling stream visually consistent across every word.
  const baseAlphabet = isArabicMode ? ARABIC_POOL : LATIN_POOL;
  const letterPool = useMemo(
    () => baseAlphabet + '0123456789',
    [baseAlphabet]
  );
  const isDarkBg = BACKGROUNDS[bgIdx]?.dark;

  // When current word changes, reset slots + clear tiles
  useEffect(() => {
    nextLetterIndexRef.current = 0;
    sinceNeededRef.current = 0;
    setFilledSlots([]);
    fallingTilesRef.current.forEach((t) => { if (t.el && t.el.parentNode) t.el.remove(); });
    fallingTilesRef.current = [];
    lastSpawnRef.current = 0;
  }, [currentWordIndex, script]);

  // ── Spawn + tick (game loop) ────────────────────────────────────────────
  const spawnTile = useCallback(() => {
    if (!currentWord || !fallAreaRef.current) return;
    const word = targetString;
    if (!word || nextLetterIndexRef.current >= word.length) return;
    const needed = word[nextLetterIndexRef.current];

    // Pure random pull from the pool — no bias toward the needed letter.
    // Soft starvation guard: if STARVE_LIMIT tiles have spawned without
    // producing `needed`, force it once so a bad RNG streak can't stall the
    // game. The guard rarely fires in practice (1/26 odds × 10 tiles ≈ 33%
    // hit rate naturally), so the falling stream still looks uniformly random.
    const STARVE_LIMIT = 10;
    let char;
    if (sinceNeededRef.current >= STARVE_LIMIT) {
      char = needed;
      sinceNeededRef.current = 0;
    } else {
      char = letterPool[Math.floor(Math.random() * letterPool.length)];
      if (char === needed) sinceNeededRef.current = 0;
      else sinceNeededRef.current++;
    }

    const rect = fallAreaRef.current.getBoundingClientRect();
    const maxX = rect.width - TILE_SIZE;
    let startX = Math.random() * maxX;
    // Anti-overlap (try a few positions)
    const SPAWN_ZONE_Y = TILE_SIZE * 1.5;
    const MIN_GAP = TILE_SIZE + 8;
    for (let attempt = 0; attempt < 8; attempt++) {
      const tryX = Math.random() * maxX;
      const collides = fallingTilesRef.current.some((t) => t.y < SPAWN_ZONE_Y && Math.abs(t.startX + (t._lastXOffset || 0) - tryX) < MIN_GAP);
      if (!collides) { startX = tryX; break; }
    }

    const tile = document.createElement('div');
    tile.className = 'lf-letter-tile' + (isArabicMode ? ' arabic' : '');
    tile.textContent = char;
    tile.style.left = startX + 'px';
    tile.style.top = '-' + TILE_SIZE + 'px';
    fallAreaRef.current.appendChild(tile);

    const pattern = [
      { sway: 0, swaySpeed: 0, speedMult: 1.0 },
      { sway: 22, swaySpeed: 0.003, speedMult: 1.0 },
      { sway: 0, swaySpeed: 0, speedMult: 0.9, drift: (Math.random() - 0.5) * 0.04 },
      { sway: 38, swaySpeed: 0.002, speedMult: 0.75 },
    ][Math.floor(Math.random() * 4)];

    const t = {
      el: tile, char, startX, x: startX, y: -TILE_SIZE,
      vy: SPEED_PRESETS[speed].fall * pattern.speedMult * (0.9 + Math.random() * 0.2),
      sway: pattern.sway, swaySpeed: pattern.swaySpeed, swayPhase: Math.random() * Math.PI * 2,
      drift: pattern.drift || 0, spawnTime: performance.now(),
    };
    fallingTilesRef.current.push(t);

    const onTap = () => onTapTile(t);
    tile.addEventListener('click', onTap);
    tile.addEventListener('touchstart', (e) => { e.preventDefault(); onTap(); }, { passive: false });
  }, [currentWord, targetString, isArabicMode, letterPool, speed]);

  const onTapTile = useCallback((tile) => {
    const word = targetString;
    const needed = word[nextLetterIndexRef.current];
    if (tile.char === needed) {
      sfxRef.current?.correct();
      tile.el.classList.add('correct');
      const idx = nextLetterIndexRef.current;
      setFilledSlots((prev) => {
        const next = [...prev];
        // Fill with the actual target char at this position (case-preserved from word)
        next[idx] = word[idx];
        return next;
      });
      nextLetterIndexRef.current++;
      setTimeout(() => {
        if (tile.el && tile.el.parentNode) tile.el.remove();
        fallingTilesRef.current = fallingTilesRef.current.filter((t) => t !== tile);
      }, 350);
      if (nextLetterIndexRef.current >= word.length) {
        setTimeout(() => { sfxRef.current?.complete(); setShowComplete(true); }, 400);
      }
    } else {
      sfxRef.current?.wrong();
      tile.el.classList.add('wrong');
      if (navigator.vibrate) navigator.vibrate(80);
      setTimeout(() => tile.el.classList.remove('wrong'), 400);
    }
  }, [targetString]);

  // Main animation loop — pauses while intro overlay is showing
  useEffect(() => {
    if (loading || errorMsg || !currentWord || showComplete || showHelp) return;
    let running = true;
    const tick = (now) => {
      if (!running) return;
      let dt = lastFrameRef.current ? now - lastFrameRef.current : 16;
      if (dt > 32) dt = 32;
      lastFrameRef.current = now;

      if (now - lastSpawnRef.current >= SPEED_PRESETS[speed].spawn && fallingTilesRef.current.length < MAX_TILES_ON_SCREEN) {
        spawnTile();
        lastSpawnRef.current = now;
      }

      const rect = fallAreaRef.current?.getBoundingClientRect();
      if (rect) {
        for (let i = fallingTilesRef.current.length - 1; i >= 0; i--) {
          const t = fallingTilesRef.current[i];
          t.y += t.vy * dt;
          let xOffset = 0;
          if (t.sway > 0) xOffset += Math.sin(t.swayPhase + (now - t.spawnTime) * t.swaySpeed) * t.sway;
          if (t.drift) xOffset += t.drift * (now - t.spawnTime);
          const targetX = Math.max(0, Math.min(rect.width - TILE_SIZE, t.startX + xOffset));
          t._lastXOffset = targetX - t.startX;
          t.el.style.transform = `translate(${t._lastXOffset}px, ${t.y + TILE_SIZE}px)`;
          if (t.y > rect.height) {
            t.el.remove();
            fallingTilesRef.current.splice(i, 1);
          }
        }
      }
      animationRef.current = requestAnimationFrame(tick);
    };
    animationRef.current = requestAnimationFrame(tick);
    return () => {
      running = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      lastFrameRef.current = 0;
    };
  }, [loading, errorMsg, currentWord, showComplete, showHelp, speed, spawnTile]);

  // Clean up tiles when leaving
  useEffect(() => {
    return () => {
      fallingTilesRef.current.forEach((t) => { if (t.el && t.el.parentNode) t.el.remove(); });
      fallingTilesRef.current = [];
    };
  }, []);

  const nextWord = () => {
    sfxRef.current?.skip();
    setShowComplete(false);
    if (words.length === 0) return;
    let next;
    do {
      next = Math.floor(Math.random() * words.length);
    } while (next === currentWordIndex && words.length > 1);
    setCurrentWordIndex(next);
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    sfxRef.current?.setEnabled(next);
    if (next) sfxRef.current?.correct();
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="lf-host" style={{ background: BACKGROUNDS[0].gradient }}>
        <style>{STYLE}</style>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Space Grotesk', fontWeight: 700, color: '#0A0F1C' }}>
          Loading Letter Fall…
        </div>
      </div>
    );
  }

  if (errorMsg === 'pro_required') {
    return (
      <div className="lf-host" style={{ background: BACKGROUNDS[0].gradient }}>
        <style>{STYLE}</style>
        <button className="lf-back-btn" onClick={onBack} style={{ position: 'absolute', top: 16, left: 16, zIndex: 100 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>🔒</div>
          <h2 style={{ fontFamily: 'Space Grotesk', fontWeight: 800, fontSize: 28, color: '#0A0F1C', margin: '0 0 12px' }}>Pro feature</h2>
          <p style={{ color: '#8A8E9A', maxWidth: 320, lineHeight: 1.6 }}>Letter Fall is a Pro game. Upgrade to play.</p>
        </div>
      </div>
    );
  }

  if (errorMsg === 'no_words') {
    return (
      <div className="lf-host" style={{ background: BACKGROUNDS[0].gradient }}>
        <style>{STYLE}</style>
        <button className="lf-back-btn" onClick={onBack} style={{ position: 'absolute', top: 16, left: 16, zIndex: 100 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>🔤</div>
          <h2 style={{ fontFamily: 'Space Grotesk', fontWeight: 800, fontSize: 24, color: '#0A0F1C', margin: '0 0 8px' }}>No words yet</h2>
          <p style={{ color: '#8A8E9A', maxWidth: 320, lineHeight: 1.6 }}>The admin hasn't added any words to Letter Fall yet. Come back soon!</p>
        </div>
      </div>
    );
  }

  if (!currentWord) return null;

  const bg = BACKGROUNDS[bgIdx];
  const targetDisplay = isArabicMode ? currentWord.arabic : currentWord.latin.toUpperCase();

  return (
    <div className={`lf-host lf-bg-${bgIdx}${isDarkBg ? ' lf-dark' : ''}`} style={{ background: bg.gradient }}>
      <style>{STYLE}</style>

      <div className="fx-layer" aria-hidden="true">
        <div className="fx-orb o1" /><div className="fx-orb o2" /><div className="fx-orb o3" /><div className="fx-orb o4" />
        <div className="fx-star s1" /><div className="fx-star s2" /><div className="fx-star s3" /><div className="fx-star s4" />
        <div className="fx-star s5" /><div className="fx-star s6" /><div className="fx-star s7" /><div className="fx-star s8" />
        <div className="shooting" />
      </div>

      {/* Top nav */}
      <header className="lf-topnav">
        <button className="lf-back-btn" onClick={onBack} aria-label="Back to games">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div className="lf-speed-switcher">
          {['slow', 'normal', 'fast'].map((s) => (
            <button key={s} className={speed === s ? 'active' : ''} onClick={() => setSpeed(s)}>
              {s === 'slow' ? '🐢 Slow' : s === 'normal' ? '🚶 Med' : '🏃 Fast'}
            </button>
          ))}
        </div>
        <div className="lf-topnav-right">
        <button className="lf-help-btn lf-help-btn-desktop" onClick={() => setShowHelp(true)} aria-label="How to play">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </button>
        <button className="lf-gear-btn" onClick={() => setDrawerOpen(true)} aria-label="Settings">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        </button>
        </div>
      </header>

      {/* Header — English on top, then Darija below */}
      <section className="lf-header">
        <div className="target-meaning">{currentWord.english}</div>
        <h1 className={`target-word${isArabicMode ? ' arabic' : ''}`}>{targetDisplay}</h1>
      </section>

      {/* Fall area */}
      <div ref={fallAreaRef} className="lf-fall-area" />

      {/* Slots — Arabic mode reverses order so first slot is on the right */}
      <div ref={slotsRef} className={`lf-slots${isArabicMode ? ' arabic' : ''}`}>
        {targetString.split('').map((ch, i) => (
          <div key={i} className={`lf-slot${isArabicMode ? ' arabic' : ''}${filledSlots[i] ? ' filled' : ''}`}>
            {filledSlots[i] || ''}
          </div>
        ))}
      </div>

      {/* Bottom */}
      <div className="lf-bottom">
        <button className="lf-btn lf-btn-skip" onClick={nextWord}>Skip word →</button>
      </div>

      {/* Settings drawer — blur active el before closing to avoid aria-hidden a11y warning */}
      <div className={`lf-drawer${drawerOpen ? ' show' : ''}`} {...(!drawerOpen && { inert: true })}>
        <div className="lf-drawer-overlay" onClick={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); setDrawerOpen(false); }} />
        <aside className="lf-drawer-panel">
          <header className="lf-drawer-head">
            <h2>Settings</h2>
            <button className="lf-drawer-close" onClick={(e) => { e.currentTarget.blur(); setDrawerOpen(false); }}>✕</button>
          </header>
          <div className="lf-drawer-body">
            <div className="lf-drawer-section">
              <div className="label">Script</div>
              <div className="lf-script-radio">
                <button className={script === 'latin' ? 'active' : ''} onClick={() => setScript('latin')}>🔤 Latin</button>
                <button className={script === 'arabic' ? 'active' : ''} onClick={() => setScript('arabic')}>ا Arabic</button>
              </div>
            </div>
            <div className="lf-drawer-section">
              <div className="label">Background</div>
              <div className="lf-bg-grid">
                {BACKGROUNDS.map((b, i) => (
                  <button key={i} data-bg={i} className={bgIdx === i ? 'active' : ''} style={{ background: b.swatch }} onClick={() => setBgIdx(i)}>
                    <span className="name">{b.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="lf-drawer-section">
              <div className="label">Audio</div>
              <div className="lf-row">
                <div>
                  <div className="label-text">Sound effects</div>
                  <div className="hint">Pop on catch, ding on complete</div>
                </div>
                <button className={`lf-toggle${soundEnabled ? ' on' : ''}`} onClick={toggleSound} aria-label="Toggle sound" />
              </div>
            </div>
            <div className="lf-drawer-section lf-drawer-help-section">
              <button
                className="lf-drawer-help-btn"
                onClick={(e) => { e.currentTarget.blur(); setDrawerOpen(false); setShowHelp(true); }}
              >
                <span className="ic" aria-hidden="true">?</span>
                <span>How to play</span>
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* How-to-play popup — re-openable via the "?" button. Same card as
          the intro but with an X close instead of the "Let's play" CTA. */}
      {showHelp && (
        <div className="lf-intro" onClick={() => setShowHelp(false)}>
          <div className="lf-intro-card" onClick={(e) => e.stopPropagation()}>
            <button
              className="lf-help-close"
              aria-label="Close"
              onClick={() => setShowHelp(false)}
            >✕</button>
            <div className="lf-intro-hero">
              <span className="lf-intro-mini a">A</span>
              <span className="lf-intro-mini b">B</span>
              <span className="lf-intro-mini c">C</span>
            </div>
            <h2>How to play</h2>
            <p className="sub">Tap the falling letters in the right order to spell the word.</p>
            <ul className="lf-intro-how">
              <li><span className="ic">📝</span><span><strong>Look at the word</strong> shown at the top</span></li>
              <li><span className="ic">👆</span><span><strong>Tap the letters</strong> as they fall — in order</span></li>
              <li><span className="ic">✅</span><span>Correct letter → slot fills in</span></li>
              <li><span className="ic">❌</span><span>Wrong letter → shakes red, try again</span></li>
            </ul>
          </div>
        </div>
      )}

      {/* Complete overlay */}
      {showComplete && (
        <div className="lf-overlay">
          <div className="lf-overlay-card">
            <div className="emoji">🎉</div>
            <div style={{ fontFamily: 'Space Grotesk', fontWeight: 700, color: '#93D3B5', letterSpacing: 1, fontSize: 12, textTransform: 'uppercase' }}>Wahed!</div>
            <div className={`word${isArabicMode ? ' arabic' : ''}`}>{targetDisplay}</div>
            <div className="meaning">{currentWord.english}</div>
            <button className="btn-primary" onClick={nextWord}>Next word →</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterFall;
